import React, { useEffect, useRef, useState } from 'react';
import { AvatarUpload, Button, Input, SparkIcon, TooltipTrigger, sparkToast, useReadOnly } from '@spark';

import SettingsSectionCard from '@/features/settings/components/SettingsSectionCard.jsx';
import { useAuth } from '@/features/auth/AuthContext';
import { updateWorkspace } from '../../api/workspacesApi';
import { LOGO_ACCEPT, cropImageToSquare, logoImageErrorText } from '../../utils/logoImage';
import useLogoCrop from '../../hooks/useLogoCrop';
import LogoCropModal from '../LogoCropModal';
import '@/features/settings/components/settings.css';
import './WorkspaceSettingsTab.css';

const NAME_MAX_LENGTH = 200;

/**
 * Settings > Workspace (Figma 9244:4940): Admins and gods edit the active
 * workspace's logo and name.  Both autosave through ``PATCH /workspaces/{id}``
 * — the logo once the user has framed it in `LogoCropModal`, the name on
 * blur / Enter when it is non-blank and differs from the saved value — and
 * the session is patched in place so the header and avatar menu refresh
 * without a reload.  The name field never locks while saving (a "Saving…"
 * helper and `aria-busy` show the state instead), so typing is not
 * interrupted by a slow PATCH.  A name another workspace already uses comes
 * back as 409 and is shown under the field — the rejected draft stays put so
 * it can be edited.  "Delete Workspace" ships disabled behind a
 * "Coming soon" tooltip.
 *
 * `cropLogo` is injectable so tests can skip the canvas pipeline.
 */
const WorkspaceSettingsTab = ({ cropLogo = cropImageToSquare }) => {
    const { workspace, role, platformRole, isAuthEnabled, applyWorkspaceUpdate } = useAuth();
    const { readOnly } = useReadOnly();
    const canEdit = !isAuthEnabled || role === 'Admin' || platformRole === 'god';
    const disabled = readOnly || !canEdit || !workspace?.id;

    const savedName = workspace?.name || '';
    const [name, setName] = useState(savedName);
    const [nameError, setNameError] = useState('');
    const [savingName, setSavingName] = useState(false);
    const [savingLogo, setSavingLogo] = useState(false);
    // The field stays editable while a rename is in flight, so a later save
    // can overtake an earlier one: each save takes a sequence number and only
    // the newest one is allowed to toast or patch the session.  `pendingName`
    // stops Enter + the blur that follows from sending the same value twice.
    // PATCHes are also sent one at a time (`nameSaveChain`): a draft that was
    // superseded while it waited is never sent, so the server always ends up
    // with the newest name rather than whichever response happened to land
    // last.
    const nameSaveSeq = useRef(0);
    const nameSaveChain = useRef(Promise.resolve());
    const pendingName = useRef(null);
    const lastSavedName = useRef(savedName);
    const lastWorkspaceId = useRef(workspace?.id);

    // Follow the session (another tab renamed it, workspace switched, save
    // confirmed) — but never over a draft the user is still typing: a rename
    // confirmed mid-keystroke must not wipe what came after it.
    useEffect(() => {
        const switched = lastWorkspaceId.current !== workspace?.id;
        lastWorkspaceId.current = workspace?.id;
        if (switched) setNameError('');
        const previous = lastSavedName.current;
        lastSavedName.current = savedName;
        setName((draft) => {
            const untouched = draft.trim() === previous || draft.trim() === savedName;
            return switched || untouched ? savedName : draft;
        });
    }, [workspace?.id, savedName]);

    const applyResult = (result, successMessage) => {
        if (!result.ok) {
            sparkToast.error(result.error);
            return false;
        }
        if (result.data?.workspace) {
            applyWorkspaceUpdate(result.data.workspace);
        }
        sparkToast.success(successMessage);
        return true;
    };

    const saveName = async () => {
        if (disabled) return;
        const trimmed = name.trim();
        if (!trimmed) {
            // Blank is never saved; snap back to what the server has.
            setName(savedName);
            setNameError('');
            return;
        }
        if (trimmed === savedName || trimmed === pendingName.current) {
            if (trimmed !== name) setName(trimmed);
            return;
        }
        nameSaveSeq.current += 1;
        const seq = nameSaveSeq.current;
        pendingName.current = trimmed;
        setNameError('');
        setSavingName(true);
        const workspaceId = workspace.id;
        const run = async () => {
            // Superseded while waiting for the previous PATCH: never send it.
            if (seq !== nameSaveSeq.current) return;
            try {
                const result = await updateWorkspace(workspaceId, { name: trimmed });
                // A newer rename superseded this one; its response is the one that counts.
                if (seq !== nameSaveSeq.current) return;
                // A taken name is shown under the field — the draft stays so the
                // user can edit it instead of retyping after a toast disappears.
                if (!result.ok && result.nameTaken) {
                    setNameError(result.error);
                    return;
                }
                applyResult(result, 'Workspace name updated');
            } finally {
                if (seq === nameSaveSeq.current) {
                    pendingName.current = null;
                    setSavingName(false);
                }
            }
        };
        const turn = nameSaveChain.current.then(run);
        // Keep the chain alive after a rejected save so later renames still go out.
        nameSaveChain.current = turn.catch(() => {});
        await turn;
    };

    const handleNameKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            saveName();
        } else if (event.key === 'Escape') {
            setName(savedName);
            setNameError('');
        }
    };

    const saveLogo = async (logo) => {
        if (disabled || !logo) return;
        setSavingLogo(true);
        try {
            const result = await updateWorkspace(workspace.id, { logo });
            applyResult(result, 'Workspace logo updated');
        } finally {
            setSavingLogo(false);
        }
    };

    // Picked file → crop dialog → `saveLogo(dataUrl)`; an unsupported file or
    // a crop failure toasts and keeps the current logo.
    const logoCrop = useLogoCrop({
        onLogo: saveLogo,
        onError: (err) => sparkToast.error(logoImageErrorText(err)),
    });

    return (
        <SettingsSectionCard iconSlug="workspace" title="Workspace">
            <div className="settings-providers__panel">
                <h3 className="settings-subheading fs-unmask">Manage Workspace</h3>

                <div className="workspace-settings__fields">
                    <AvatarUpload
                        label="Workspace Image"
                        emptyVariant="initials"
                        initials={savedName}
                        src={workspace?.logo || null}
                        alt="Workspace logo"
                        accept={LOGO_ACCEPT}
                        disabled={disabled || savingLogo}
                        onFileSelected={logoCrop.pickLogo}
                    />
                    <LogoCropModal {...logoCrop.modalProps} cropLogo={cropLogo} />

                    <Input
                        id="workspace-settings-name"
                        className="workspace-settings__name"
                        size="medium"
                        label="Workspace Name"
                        placeholder="Name of company workspace"
                        value={name}
                        maxLength={NAME_MAX_LENGTH}
                        disabled={disabled}
                        aria-busy={savingName || undefined}
                        danger={Boolean(nameError)}
                        showHelperText={savingName || Boolean(nameError)}
                        helperText={nameError || 'Saving…'}
                        onChange={(event) => {
                            setName(event.target.value);
                            if (nameError) setNameError('');
                        }}
                        onBlur={saveName}
                        onKeyDown={handleNameKeyDown}
                        autoComplete="organization"
                    />
                </div>

                <TooltipTrigger text="Coming soon" placement="bottom" className="workspace-settings__delete">
                    <Button
                        variant="secondary"
                        size="medium"
                        disabled
                        startIcon={<SparkIcon slug="trash" size="medium" />}
                    >
                        Delete Workspace
                    </Button>
                </TooltipTrigger>
            </div>
        </SettingsSectionCard>
    );
};

export default WorkspaceSettingsTab;
